import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusCircle, X, Loader2, AlertCircle, DollarSign, MapPin, Clock, Trash2, Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

// Currency symbols mapping
const CURRENCY_CODES = {
  'PLN': 'PLN',
  'EUR': 'EUR',
  'CHF': 'CHF',
  'GBP': 'GBP',
  'USD': 'USD',
  'DKK': 'DKK',
  'SEK': 'SEK',
  'NOK': 'NOK',
  'ALL': 'ALL',
  'BGN': 'BGN',
  'BYN': 'BYN',
  'CZK': 'CZK',
  'GEL': 'GEL',
  'HUF': 'HUF',
  'ISK': 'ISK',
  'MDL': 'MDL',
  'MKD': 'MKD',
  'RON': 'RON',
  'RSD': 'RSD',
  'RUB': 'RUB',
  'TRY': 'TRY',
  'UAH': 'UAH'
};

// Currency choices for dropdown
const CURRENCY_CHOICES = [
  { value: 'PLN', label: 'Polish Zloty (PLN)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'CHF', label: 'Swiss Franc (CHF)' },
  { value: 'GBP', label: 'British Pound (GBP)' },
  { value: 'USD', label: 'US Dollar (USD)' },
  { value: 'DKK', label: 'Danish Krone (DKK)' },
  { value: 'SEK', label: 'Swedish Krona (SEK)' },
  { value: 'NOK', label: 'Norwegian Krone (NOK)' },

  // Reszta alfabetycznie po kodzie
  { value: 'ALL', label: 'Albanian Lek (ALL)' },
  { value: 'BGN', label: 'Bulgarian Lev (BGN)' },
  { value: 'BYN', label: 'Belarusian Ruble (BYN)' },
  { value: 'CZK', label: 'Czech Koruna (CZK)' },
  { value: 'GEL', label: 'Georgian Lari (GEL)' },
  { value: 'HUF', label: 'Hungarian Forint (HUF)' },
  { value: 'ISK', label: 'Icelandic Krona (ISK)' },
  { value: 'MDL', label: 'Moldovan Leu (MDL)' },
  { value: 'MKD', label: 'Macedonian Denar (MKD)' },
  { value: 'RON', label: 'Romanian Leu (RON)' },
  { value: 'RSD', label: 'Serbian Dinar (RSD)' },
  { value: 'RUB', label: 'Russian Ruble (RUB)' },
  { value: 'TRY', label: 'Turkish Lira (TRY)' },
  { value: 'UAH', label: 'Ukrainian Hryvnia (UAH)' }
];

const TrainingManagementContent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [trainings, setTrainings] = useState([]);
  const [filteredTrainings, setFilteredTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    training_center_id: '',
    qualification_type_id: '',
    price: '',
    currency: 'PLN',
    specific_address: '',
    is_available: true,
    training_duration_days: '',
    description: '',
    required_items: '',
  });
  const [trainingCenters, setTrainingCenters] = useState([]);
  const [qualificationTypes, setQualificationTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCenter, setFilterCenter] = useState('');
  const [filterQualification, setFilterQualification] = useState('');
  const [centerSearch, setCenterSearch] = useState('');
  const [qualificationSearch, setQualificationSearch] = useState('');
  const [showCenterDropdown, setShowCenterDropdown] = useState(false);
  const [showQualificationDropdown, setShowQualificationDropdown] = useState(false);
  const [searchOptions, setSearchOptions] = useState({ qualifications: [], locations: [] });
  const [modalError, setModalError] = useState(null);

  const fetchSearchOptions = async () => {
    try {
      // TODO: Implement search_options endpoint in backend
      // For now, use data from existing endpoints
      const trainingsResponse = await axios.get(`${API_BASE_URL}/training-center-qualifications/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      
      // Extract unique locations and qualifications
      const uniqueLocations = [...new Set(trainingsResponse.data.map(t => t.location).filter(Boolean))];
      const uniqueQualifications = [...new Set(trainingsResponse.data.map(t => t.qualification_data?.name).filter(Boolean))];
      
      setSearchOptions({
        locations: uniqueLocations,
        qualifications: uniqueQualifications
      });
    } catch (err) {
      console.error('Failed to fetch search options:', err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const trainingsResponse = await axios.get(`${API_BASE_URL}/training-center-qualifications/`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        });
        setTrainings(trainingsResponse.data);
        setFilteredTrainings(trainingsResponse.data);

        const centersResponse = await axios.get(`${API_BASE_URL}/training-centers/`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        });
        setTrainingCenters(centersResponse.data);

        const qualificationsResponse = await axios.get(`${API_BASE_URL}/company-qualifications/`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        });
        setQualificationTypes(qualificationsResponse.data);

        // Fetch search options for enhanced search
        await fetchSearchOptions();

        setLoading(false);
      } catch (err) {
        setError('Failed to fetch training data');
        setLoading(false);
        console.error(err);
      }
    };

    fetchData();
  }, []);

  // Listen for the triggerAdd event from the parent component
  useEffect(() => {
    const handleTriggerAdd = (event) => {
      if (event.detail.tab === 'trainings') {
        setIsEditing(false);
        setShowModal(true);
      }
    };

    window.addEventListener('triggerAdd', handleTriggerAdd);
    return () => {
      window.removeEventListener('triggerAdd', handleTriggerAdd);
    };
  }, []);

  // Check URL parameters to auto-open modal
  useEffect(() => {
    const addNew = searchParams.get('addNew');
    const qualificationTypeId = searchParams.get('qualificationTypeId');
    
    console.log('Checking URL params:', { addNew, qualificationTypeId, qualificationTypesLength: qualificationTypes.length });
    
    if (addNew === 'true' && qualificationTypes.length > 0) {
      console.log('Opening modal for new training...');
      setShowModal(true);
      setIsEditing(false);
      
      // Pre-fill qualification type if provided
      if (qualificationTypeId) {
        const qualificationTypeIdInt = parseInt(qualificationTypeId);
        console.log('Pre-filling qualification type ID:', qualificationTypeIdInt);
        
        setFormData(prev => ({
          ...prev,
          qualification_type_id: qualificationTypeIdInt
        }));
        
        // Find and set the qualification search term for display
        const qualificationType = qualificationTypes.find(q => q.id === qualificationTypeIdInt);
        if (qualificationType) {
          setQualificationSearch(qualificationType.qualification_type_data.name);
          console.log('Auto-selected qualification type:', qualificationType.qualification_type_data.name);
        } else {
          console.log('Qualification type not found for ID:', qualificationTypeIdInt);
        }
      }
      
      // Clear URL parameters after processing
      setSearchParams(new URLSearchParams());
    }
  }, [searchParams, qualificationTypes, setSearchParams]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setShowCenterDropdown(false);
        setShowQualificationDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch filtered data from backend
  const fetchFilteredData = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);

      const response = await axios.get(`${API_BASE_URL}/training-center-qualifications/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      
      // For training management, we still show training center qualifications
      // but we can use the search to help find related data
      let filtered = [...trainings];

      if (searchTerm) {
        filtered = filtered.filter(
          (training) =>
            training.training_center.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            training.qualification_type.qualification_type_data.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            training.training_center.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            training.specific_address?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      if (filterCenter) {
        filtered = filtered.filter((training) => training.training_center.id === parseInt(filterCenter));
      }

      if (filterQualification) {
        filtered = filtered.filter(
          (training) => training.qualification_type.id === parseInt(filterQualification)
        );
      }


      setFilteredTrainings(filtered);
    } catch (err) {
      console.error('Failed to fetch filtered data:', err);
    }
  };

  useEffect(() => {
    if (trainings.length > 0) {
      fetchFilteredData();
    }
  }, [searchTerm, filterCenter, filterQualification, trainings]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'is_available' ? value === 'true' : value,
    }));
  };

  const resetForm = () => {
    setFormData({
      id: null,
      training_center_id: '',
      qualification_type_id: '',
      price: '',
      currency: 'PLN',
      specific_address: '',
      is_available: true,
      training_duration_days: '',
      description: '',
      required_items: '',
    });
    setShowModal(false);
    setIsEditing(false);
    setShowDeleteConfirm(false);
    setCenterSearch('');
    setQualificationSearch('');
    setShowCenterDropdown(false);
    setShowQualificationDropdown(false);
    setModalError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      };

      const dataToSend = {
        price: formData.price,
        currency: formData.currency,
        specific_address: formData.specific_address,
        is_available: formData.is_available,
        training_duration_days: formData.training_duration_days || null,
        description: formData.description || '',
        required_items: formData.required_items || '',
      };

      if (isEditing) {
        const response = await axios.patch(
          `${API_BASE_URL}/training-center-qualifications/${formData.id}/`,
          dataToSend,
          config
        );
        setTrainings((prev) =>
          prev.map((training) => (training.id === response.data.id ? response.data : training))
        );
      } else {
        dataToSend.training_center_id = formData.training_center_id;
        dataToSend.qualification_type_id = formData.qualification_type_id;
        const response = await axios.post(`${API_BASE_URL}/training-center-qualifications/`, dataToSend, config);
        setTrainings((prev) => [...prev, response.data]);
      }

      resetForm();
    } catch (err) {
      console.error(err);
      
      // Check for unique constraint error
      if (err.response && err.response.status === 400) {
        const errorData = err.response.data;
        if (errorData.non_field_errors) {
          setModalError(errorData.non_field_errors[0]);
        } else {
          setModalError(`Failed to ${isEditing ? 'update' : 'add'} training: ${JSON.stringify(errorData)}`);
        }
      } else if (err.response && err.response.status === 500 && err.response.data) {
        // Handle IntegrityError
        if (err.response.data.includes('UNIQUE constraint failed')) {
          setModalError('This training center already has this qualification type. Each center can only have one training for each qualification type.');
        } else {
          setModalError(`Failed to ${isEditing ? 'update' : 'add'} training`);
        }
      } else {
        setModalError(`Failed to ${isEditing ? 'update' : 'add'} training`);
      }
      
      // Show error for 8 seconds
      setTimeout(() => setModalError(null), 8000);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteTraining = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/training-center-qualifications/${formData.id}/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      setTrainings((prev) => prev.filter((training) => training.id !== formData.id));
      resetForm();
    } catch (err) {
      setModalError('Failed to delete training');
      console.error(err);
      setTimeout(() => setModalError(null), 5000);
    }
  };

  const handleEditTraining = (training) => {
    setFormData({
      id: training.id,
      training_center_id: training.training_center.id,
      qualification_type_id: training.qualification_type.id,
      price: training.price,
      currency: training.currency || 'PLN',
      specific_address: training.specific_address || '',
      is_available: training.is_available,
      training_duration_days: training.training_duration_days || '',
      description: training.description || '',
      required_items: training.required_items || '',
    });
    setCenterSearch(training.training_center.name);
    setQualificationSearch(training.qualification_type.qualification_type_data.name);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-steel-50 rounded-xl shadow">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-brand-500 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading trainings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="px-6 pt-4 pb-2 space-y-4">
        {/* Search and filters row */}
        <div className="flex gap-4">
          {/* Search - 1/2 width */}
          <div className="w-1/2 relative">
            <Search className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search training centers and qualifications..."
              className="block w-full border border-steel-300 rounded-xl pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
            />
          </div>
          
          {/* All centers select - 1/4 width with search */}
          <div className="w-1/4 relative dropdown-container">
            <input
              type="text"
              value={centerSearch || trainingCenters.find(c => c.id === parseInt(filterCenter))?.name || ''}
              onChange={(e) => {
                setCenterSearch(e.target.value);
                setShowCenterDropdown(true);
                if (!e.target.value) {
                  setFilterCenter('');
                }
              }}
              onFocus={() => setShowCenterDropdown(true)}
              placeholder="All centers"
              className="block w-full border border-steel-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
            />
            {showCenterDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                <div
                  onClick={() => {
                    setFilterCenter('');
                    setCenterSearch('');
                    setShowCenterDropdown(false);
                  }}
                  className="px-4 py-2 hover:bg-steel-100 cursor-pointer text-sm text-slate-500"
                >
                  All centers
                </div>
                {trainingCenters
                  .filter(center => center.name.toLowerCase().includes((centerSearch || '').toLowerCase()))
                  .map((center) => (
                    <div
                      key={center.id}
                      onClick={() => {
                        setFilterCenter(center.id.toString());
                        setCenterSearch(center.name);
                        setShowCenterDropdown(false);
                      }}
                      className="px-4 py-2 hover:bg-steel-100 cursor-pointer text-sm"
                    >
                      {center.name}
                    </div>
                  ))}
                {trainingCenters.filter(center => center.name.toLowerCase().includes((centerSearch || '').toLowerCase())).length === 0 && centerSearch && (
                  <div className="px-4 py-2 text-slate-500 text-sm">No centers found</div>
                )}
              </div>
            )}
          </div>
          
          {/* All qualifications select - 1/4 width with search */}
          <div className="w-1/4 relative dropdown-container">
            <input
              type="text"
              value={qualificationSearch || qualificationTypes.find(q => q.id === parseInt(filterQualification))?.qualification_type_data.name || ''}
              onChange={(e) => {
                setQualificationSearch(e.target.value);
                setShowQualificationDropdown(true);
                if (!e.target.value) {
                  setFilterQualification('');
                }
              }}
              onFocus={() => setShowQualificationDropdown(true)}
              placeholder="All qualifications"
              className="block w-full border border-steel-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
            />
            {showQualificationDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                <div
                  onClick={() => {
                    setFilterQualification('');
                    setQualificationSearch('');
                    setShowQualificationDropdown(false);
                  }}
                  className="px-4 py-2 hover:bg-steel-100 cursor-pointer text-sm text-slate-500"
                >
                  All qualifications
                </div>
                {qualificationTypes
                  .filter(type => type.qualification_type_data.name.toLowerCase().includes((qualificationSearch || '').toLowerCase()))
                  .sort((a, b) => a.qualification_type_data.name.localeCompare(b.qualification_type_data.name))
                  .map((type) => (
                    <div
                      key={type.id}
                      onClick={() => {
                        setFilterQualification(type.id.toString());
                        setQualificationSearch(type.qualification_type_data.name);
                        setShowQualificationDropdown(false);
                      }}
                      className="px-4 py-2 hover:bg-steel-100 cursor-pointer text-sm"
                    >
                      {type.qualification_type_data.name}
                    </div>
                  ))}
                {qualificationTypes.filter(type => type.qualification_type_data.name.toLowerCase().includes((qualificationSearch || '').toLowerCase())).length === 0 && qualificationSearch && (
                  <div className="px-4 py-2 text-slate-500 text-sm">No qualification types found</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto">
          <table className="min-w-full divide-y divide-steel-200">
            <thead className="bg-steel-100 sticky top-0 z-[5] shadow-sm">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider border-b-2 border-brand-500">
                  Training Center
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider border-b-2 border-brand-500">
                  Qualification Type
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider border-b-2 border-brand-500">
                  Price
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider border-b-2 border-brand-500">
                  Specific Address
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider border-b-2 border-brand-500">
                  Availability
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider border-b-2 border-brand-500">
                  Duration
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-steel-200">
              {filteredTrainings.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="max-w-sm mx-auto">
                      <div className="bg-steel-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="h-8 w-8 text-slate-400" />
                      </div>
                      <p className="text-slate-600 font-medium mb-2">No trainings found</p>
                      <p className="text-slate-500 text-sm mb-4">
                        {searchTerm || filterCenter || filterQualification
                          ? 'No results for the selected filters.'
                          : 'Add your first training to see it on the list.'}
                      </p>
                      {!searchTerm && !filterCenter && !filterQualification && (
                        <button
                          onClick={() => setShowModal(true)}
                          className="bg-brand-500 text-white px-4 py-2 rounded-md hover:bg-brand-600 transition-colors inline-flex items-center"
                        >
                          <PlusCircle className="h-4 w-4 mr-2" />
                          Add Now
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredTrainings.map((training) => (
                  <tr
                    key={training.id}
                    className="hover:bg-brand-50 transition-colors cursor-pointer"
                    onDoubleClick={() => handleEditTraining(training)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-slate-900">
                        {training.training_center.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-700">{training.qualification_type.qualification_type_data.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-700 flex items-center">
                        <span className="mr-1">{CURRENCY_CODES[training.currency] || training.currency}</span>
                        <span>{training.price}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-700 flex items-start">
                        <MapPin className="h-4 w-4 text-slate-400 mr-1 mt-0.5 flex-shrink-0" />
                        <span>{training.specific_address || 'None'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            training.is_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {training.is_available ? 'Available' : 'Unavailable'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-700 flex items-center">
                        <Clock className="h-4 w-4 text-slate-400 mr-1" />
                        <span>{training.training_duration_days ? `${training.training_duration_days} days` : 'None'}</span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] p-0 overflow-hidden flex flex-col">
            <div className="bg-brand-500 text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold">
                {isEditing ? 'Edit Training' : 'Add New Training'}
              </h3>
              <button
                onClick={resetForm}
                className="text-white bg-brand-400 hover:bg-brand-600 rounded-full p-1 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              {/* Modal Error Display */}
              {modalError && (
                <div className="mx-6 mt-4 mb-2 bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{modalError}</p>
                    </div>
                    <div className="ml-auto pl-3">
                      <button
                        onClick={() => setModalError(null)}
                        className="text-red-400 hover:text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                {!isEditing ? (
                  <>
                    <div className="relative dropdown-container">
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Training Center
                      </label>
                      <input
                        type="text"
                        value={centerSearch}
                        onChange={(e) => {
                          setCenterSearch(e.target.value);
                          setShowCenterDropdown(true);
                        }}
                        onFocus={() => setShowCenterDropdown(true)}
                        placeholder="Search training centers..."
                        className="block w-full border border-steel-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                        required
                      />
                      {showCenterDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          {trainingCenters
                            .filter(center => center.name.toLowerCase().includes(centerSearch.toLowerCase()))
                            .map((center) => (
                              <div
                                key={center.id}
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, training_center_id: center.id }));
                                  setCenterSearch(center.name);
                                  setShowCenterDropdown(false);
                                }}
                                className="px-4 py-2 hover:bg-steel-100 cursor-pointer text-sm"
                              >
                                {center.name}
                              </div>
                            ))}
                          {trainingCenters.filter(center => center.name.toLowerCase().includes(centerSearch.toLowerCase())).length === 0 && (
                            <div className="px-4 py-2 text-slate-500 text-sm">No centers found</div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="relative dropdown-container">
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Qualification Type
                      </label>
                      <input
                        type="text"
                        value={qualificationSearch}
                        onChange={(e) => {
                          setQualificationSearch(e.target.value);
                          setShowQualificationDropdown(true);
                        }}
                        onFocus={() => setShowQualificationDropdown(true)}
                        placeholder="Search qualification types..."
                        className="block w-full border border-steel-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                        required
                      />
                      {showQualificationDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          {qualificationTypes
                            .filter(type => type.qualification_type_data.name.toLowerCase().includes(qualificationSearch.toLowerCase()))
                            .sort((a, b) => a.qualification_type_data.name.localeCompare(b.qualification_type_data.name))
                            .map((type) => (
                              <div
                                key={type.id}
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, qualification_type_id: type.id }));
                                  setQualificationSearch(type.qualification_type_data.name);
                                  setShowQualificationDropdown(false);
                                }}
                                className="px-4 py-2 hover:bg-steel-100 cursor-pointer text-sm"
                              >
                                {type.qualification_type_data.name}
                              </div>
                            ))}
                          {qualificationTypes.filter(type => type.qualification_type_data.name.toLowerCase().includes(qualificationSearch.toLowerCase())).length === 0 && (
                            <div className="px-4 py-2 text-slate-500 text-sm">No qualification types found</div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Training Center
                      </label>
                      <input
                        type="text"
                        value={trainingCenters.find((c) => c.id === formData.training_center_id)?.name || ''}
                        className="block w-full border border-steel-300 rounded-xl px-4 py-3 bg-steel-100 text-slate-700"
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Qualification Type
                      </label>
                      <input
                        type="text"
                        value={
                          qualificationTypes.find((t) => t.id === formData.qualification_type_id)?.qualification_type_data.name || ''
                        }
                        className="block w-full border border-steel-300 rounded-xl px-4 py-3 bg-steel-100 text-slate-700"
                        disabled
                      />
                    </div>
                  </>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Price</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400">
                        {CURRENCY_CODES[formData.currency] || formData.currency}
                      </span>
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        className="block w-full border border-steel-300 rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                        placeholder="Enter price"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Currency</label>
                    <select
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                      className="block w-full border border-steel-300 rounded-xl px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                      required
                    >
                      {CURRENCY_CHOICES.map((currency) => (
                        <option key={currency.value} value={currency.value}>
                          {currency.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Specific Address (optional)
                  </label>
                  <textarea
                    name="specific_address"
                    value={formData.specific_address}
                    onChange={handleInputChange}
                    className="block w-full border border-steel-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                    rows="3"
                    placeholder="Enter specific address (if different from center)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Availability</label>
                  <select
                    name="is_available"
                    value={formData.is_available}
                    onChange={handleInputChange}
                    className="block w-full border border-steel-300 rounded-xl px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                  >
                    <option value={true}>Available</option>
                    <option value={false}>Unavailable</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Duration (days, optional)
                  </label>
                  <div className="relative">
                    <Clock className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="number"
                      name="training_duration_days"
                      value={formData.training_duration_days}
                      onChange={handleInputChange}
                      className="block w-full border border-steel-300 rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                      placeholder="Enter number of days"
                      min="1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="block w-full border border-steel-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                    rows="3"
                    placeholder="Enter training description, additional details, or special instructions..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Required Items to Bring (optional)
                  </label>
                  <textarea
                    name="required_items"
                    value={formData.required_items}
                    onChange={handleInputChange}
                    className="block w-full border border-steel-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                    rows="3"
                    placeholder="e.g., Safety boots, helmet, passport, valid certificates, personal protective equipment..."
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    These requirements will be shown to technicians when scheduling this training
                  </p>
                </div>
              </div>

              <div className="flex justify-between items-center p-6 border-t bg-steel-50">
                {isEditing && (
                  <button
                    type="button"
                    onClick={handleDeleteClick}
                    className="bg-red-600 text-white px-5 py-2 rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center shadow-sm"
                  >
                    <Trash2 className="h-5 w-5 mr-2" />
                    Delete Training
                  </button>
                )}
                <div className="flex space-x-4 ml-auto">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-white text-slate-700 border border-steel-300 px-5 py-2 rounded-xl hover:bg-steel-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-brand-500 text-white px-5 py-2 rounded-xl hover:bg-brand-600 transition-colors font-medium shadow-sm"
                  >
                    {isEditing ? 'Save Changes' : 'Add Training'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-center mb-4">
              <AlertCircle className="h-12 w-12 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 text-center mb-2">
              Confirm Deletion
            </h3>
            <p className="text-slate-600 text-center mb-6">
              Are you sure you want to delete the training{' '}
              <span className="font-semibold">
                "{qualificationTypes.find((t) => t.id === formData.qualification_type_id)?.qualification_type_data.name}"{' '}
                at "{trainingCenters.find((c) => c.id === formData.training_center_id)?.name}"?
              </span>{' '}
              This action cannot be undone.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="bg-white text-slate-700 border border-steel-300 px-5 py-2 rounded-xl hover:bg-steel-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTraining}
                className="bg-red-600 text-white px-5 py-2 rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center"
              >
                <Trash2 className="h-5 w-5 mr-2" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingManagementContent;